/**
 * Consent copy, one entry per live marketing locale (DIT-402).
 *
 * Deliberately NOT in either i18n system. The landing runs two of them — the
 * marketing instances (flat keys, per-locale i18next instances) and the funnel
 * provider (nested keys, its own bundle directories) — and the consent banner is
 * initialised once outside React, before either is mounted, so it can belong to
 * neither. The library takes its own translations map keyed by language code,
 * which is exactly the shape below.
 *
 * The category descriptions say what a category is FOR, never which services
 * happen to sit in it today: that would go stale in 13 languages the day one is
 * added or removed. What each category actually sets is in the cookie tables in
 * `index.ts`, row by row.
 */

export interface ConsentCopy {
  title: string;
  description: string;
  acceptAll: string;
  rejectAll: string;
  manage: string;
  prefTitle: string;
  save: string;
  close: string;
  introTitle: string;
  introBody: string;
  necessaryTitle: string;
  necessaryBody: string;
  analyticsTitle: string;
  analyticsBody: string;
  marketingTitle: string;
  marketingBody: string;
  preferencesTitle: string;
  preferencesBody: string;
  colName: string;
  colPurpose: string;
  colExpiry: string;
  localeCookie: string;
  consentCookie: string;
  expiry12m: string;
  expiry6m: string;
  signupState: string;
  supportChat: string;
  expiryStorage: string;
  gaVisitor: string;
  gaSession: string;
  clarityVisitor: string;
  claritySession: string;
  clarityId: string;
  adsClick: string;
  metaBrowser: string;
  metaClick: string;
  expiry24m: string;
  expiry90d: string;
  expiry1d: string;
  adsFromAd: string;
  remarketingId: string;
  remarketingProbe: string;
  expiry13m: string;
  expiry15min: string;
  attributionCampaign: string;
  attributionClick: string;
  eventMarkers: string;
  expiryStorage90d: string;
}

/**
 * Keys whose English changed after the other locales were last translated
 * (DIT-517 review). Those locales still carry the previous meaning, and a
 * translation check cannot tell: a stale translation is neither empty nor equal to
 * the English, so both checks in `inventory.test.ts` pass it. The locales are redone
 * in one pass after the legal review, together with the privacy policy's
 * translations, and this list is what that pass works from.
 *
 * `translationFreshness.test.ts` holds it true in both directions: it compares the
 * English with a copy of the English as it was when the locales were translated, so
 * an English edit left off this list fails, and so does a list the pass forgot to
 * empty. The pass empties it and refreshes that copy together.
 */
export const ENGLISH_CHANGED_SINCE_TRANSLATION: readonly (keyof ConsentCopy)[] = [
  'description',
  'analyticsBody',
  'marketingTitle',
  'marketingBody',
  'clarityId',
  'metaBrowser',
  // DIT-513: the banner rows for di_attr_v2 and di_evt_*, machine-translated for
  // now and redone from the English that legal signs off.
  'attributionCampaign',
  'attributionClick',
  'eventMarkers',
  'expiryStorage90d',
];

export const CONSENT_COPY: Record<string, ConsentCopy> = {
  en: {
    title: 'We use cookies',
    description:
      'Some cookies are needed for the site to work. With your permission we would also use them to understand how the site is used and for our advertising.',
    acceptAll: 'Accept all',
    rejectAll: 'Reject non-essential',
    manage: 'Manage preferences',
    prefTitle: 'Cookie preferences',
    save: 'Save preferences',
    close: 'Close',
    introTitle: 'How we use cookies',
    introBody:
      'Necessary cookies are always on, because the site cannot work without them. Everything else stays off until you turn it on, and you can change your choice at any time.',
    necessaryTitle: 'Strictly necessary',
    necessaryBody:
      'Remember the language you chose, keep the signup you are completing, and store your cookie choice. These cannot be switched off.',
    analyticsTitle: 'Analytics',
    analyticsBody:
      'Help us see which pages are used and where visitors get stuck, so we can improve them. This includes recordings of how a page is used, with anything you type masked, and measurements that also reach our Google Ads account.',
    marketingTitle: 'Advertising',
    marketingBody:
      'Used to measure which of our advertisements lead to a signup, and to show our advertisements to people who have visited us, on other sites too. After you register, a hashed form of your e-mail address is used to match the signup to an advertisement.',
    preferencesTitle: 'Preferences',
    preferencesBody:
      'Remember optional choices you make, so the site behaves the way you left it. They also keep your support-chat conversation, so you can pick it up where you left off.',
    colName: 'Name',
    colPurpose: 'Purpose',
    colExpiry: 'Expiry',
    localeCookie: 'The language you chose for the site.',
    consentCookie: 'Your cookie choices.',
    expiry12m: '12 months',
    expiry6m: '6 months',
    signupState: 'The signup you are part-way through.',
    supportChat: 'Your support-chat conversation, so it continues where you left off.',
    expiryStorage: 'Local storage, until cleared',
    gaVisitor:
      'Whether you have been here before, so a returning visitor is not counted as a new one.',
    gaSession: 'Keeps one visit together, so pages read one after another count once.',
    clarityVisitor: 'Recognises your browser between visits, so we can see where people get stuck.',
    claritySession: 'Marks the visit you are in now.',
    clarityId:
      'Recognises your browser across the sites that use Clarity, the service that makes those measurements.',
    adsClick: 'Links a signup back to the advertisement it came from.',
    metaBrowser:
      'Recognises your browser, so we can measure our advertising on Facebook and Instagram and show our advertisements there to people who have visited us.',
    metaClick: 'Set when you arrive from an advertisement on Facebook or Instagram.',
    expiry24m: '24 months',
    expiry90d: '90 days',
    expiry1d: '1 day',
    adsFromAd: 'Set when you arrive from a Google advertisement, and ties this visit to it.',
    remarketingId:
      'Recognises your browser on other sites, so we can show our advertisements to people who have visited us.',
    remarketingProbe: 'Checks whether your browser accepts advertising cookies at all.',
    expiry13m: '13 months',
    expiry15min: '15 minutes',
    attributionCampaign: 'The campaign that brought you here, so your signup can be linked to it.',
    attributionClick:
      'The advertisement you clicked to get here, so your signup can be linked to it.',
    eventMarkers: 'Remembers that your signup was already counted, so it is not counted twice.',
    expiryStorage90d: 'Local storage, up to 90 days',
  },

  nl: {
    title: 'We gebruiken cookies',
    description:
      'Sommige cookies zijn nodig om de site te laten werken. Met uw toestemming gebruiken we ze ook om te begrijpen hoe de site wordt gebruikt en om onze marketing te meten.',
    acceptAll: 'Alles accepteren',
    rejectAll: 'Niet-essentiële weigeren',
    manage: 'Voorkeuren beheren',
    prefTitle: 'Cookievoorkeuren',
    save: 'Voorkeuren opslaan',
    close: 'Sluiten',
    introTitle: 'Hoe we cookies gebruiken',
    introBody:
      'Noodzakelijke cookies staan altijd aan, omdat de site zonder deze niet werkt. Al het andere blijft uit tot u het aanzet, en u kunt uw keuze altijd wijzigen.',
    necessaryTitle: 'Strikt noodzakelijk',
    necessaryBody:
      'Onthouden welke taal u koos, de registratie die u aan het invullen bent bewaren, en uw cookiekeuze opslaan. Deze kunnen niet worden uitgeschakeld.',
    analyticsTitle: 'Analytisch',
    analyticsBody:
      'Helpen ons te zien welke pagina’s worden gebruikt en waar bezoekers vastlopen, zodat we ze kunnen verbeteren.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Gebruikt om onze advertenties te meten en om inhoud te tonen die voor u relevant is.',
    preferencesTitle: 'Voorkeuren',
    preferencesBody:
      'Onthouden optionele keuzes die u maakt, zodat de site werkt zoals u hem achterliet. Ze bewaren ook uw supportchat-gesprek, zodat u verder kunt waar u gebleven was.',
    colName: 'Naam',
    colPurpose: 'Doel',
    colExpiry: 'Vervalt',
    localeCookie: 'De taal die u voor de site koos.',
    consentCookie: 'Uw cookiekeuzes.',
    expiry12m: '12 maanden',
    expiry6m: '6 maanden',
    signupState: 'De registratie die u aan het afronden bent.',
    supportChat: 'Uw supportchatgesprek, zodat het verdergaat waar u gebleven was.',
    expiryStorage: 'Lokale opslag, tot u het wist',
    gaVisitor:
      'Of u hier eerder bent geweest, zodat een terugkerende bezoeker niet als nieuw wordt geteld.',
    gaSession: 'Houdt één bezoek bij elkaar, zodat pagina’s na elkaar als één bezoek tellen.',
    clarityVisitor: 'Herkent uw browser tussen bezoeken, zodat we zien waar bezoekers vastlopen.',
    claritySession: 'Markeert het bezoek waarin u nu bent.',
    clarityId: 'Geplaatst door clarity.ms, de dienst die deze metingen doet.',
    adsClick: 'Koppelt een registratie aan de advertentie waar die vandaan kwam.',
    metaBrowser:
      'Herkent uw browser, zodat we onze advertenties op Facebook en Instagram kunnen meten.',
    metaClick: 'Geplaatst wanneer u via een advertentie op Facebook of Instagram binnenkomt.',
    expiry24m: '24 maanden',
    expiry90d: '90 dagen',
    expiry1d: '1 dag',
    adsFromAd:
      'Geplaatst wanneer u via een Google-advertentie binnenkomt, en koppelt dit bezoek eraan.',
    remarketingId:
      'Herkent uw browser op andere sites, zodat we onze advertenties kunnen tonen aan mensen die ons hebben bezocht.',
    remarketingProbe: 'Controleert of uw browser advertentiecookies überhaupt accepteert.',
    expiry13m: '13 maanden',
    expiry15min: '15 minuten',
    attributionCampaign:
      'De campagne die u hierheen bracht, zodat uw registratie eraan gekoppeld kan worden.',
    attributionClick:
      'De advertentie waarop u klikte om hier te komen, zodat uw registratie eraan gekoppeld kan worden.',
    eventMarkers: 'Onthoudt dat uw registratie al is geteld, zodat die niet twee keer telt.',
    expiryStorage90d: 'Lokale opslag, maximaal 90 dagen',
  },

  'fr-be': {
    title: 'Nous utilisons des cookies',
    description:
      'Certains cookies sont nécessaires au fonctionnement du site. Avec votre accord, nous les utiliserions aussi pour comprendre comment le site est utilisé et pour mesurer notre marketing.',
    acceptAll: 'Tout accepter',
    rejectAll: 'Refuser les non essentiels',
    manage: 'Gérer les préférences',
    prefTitle: 'Préférences de cookies',
    save: 'Enregistrer les préférences',
    close: 'Fermer',
    introTitle: 'Comment nous utilisons les cookies',
    introBody:
      'Les cookies nécessaires sont toujours actifs, car le site ne peut pas fonctionner sans eux. Tout le reste reste désactivé tant que vous ne l’activez pas, et vous pouvez modifier votre choix à tout moment.',
    necessaryTitle: 'Strictement nécessaires',
    necessaryBody:
      'Mémorisent la langue choisie, conservent l’inscription en cours et enregistrent votre choix de cookies. Ils ne peuvent pas être désactivés.',
    analyticsTitle: 'Analytiques',
    analyticsBody:
      'Nous aident à voir quelles pages sont utilisées et où les visiteurs bloquent, afin de les améliorer.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Utilisés pour mesurer notre publicité et afficher du contenu qui vous concerne.',
    preferencesTitle: 'Préférences',
    preferencesBody:
      'Mémorisent les choix facultatifs que vous faites, afin que le site reste comme vous l’avez laissé. Ils conservent aussi votre conversation avec le support, pour la reprendre là où vous l’avez laissée.',
    colName: 'Nom',
    colPurpose: 'Finalité',
    colExpiry: 'Expiration',
    localeCookie: 'La langue que vous avez choisie pour le site.',
    consentCookie: 'Vos choix de cookies.',
    expiry12m: '12 mois',
    expiry6m: '6 mois',
    signupState: "L'inscription que vous êtes en train de compléter.",
    supportChat:
      "Votre conversation avec l'assistance, pour la reprendre là où vous l'aviez laissée.",
    expiryStorage: "Stockage local, jusqu'à effacement",
    gaVisitor:
      "Si vous avez déjà visité le site, afin qu'une visite de retour ne soit pas comptée comme nouvelle.",
    gaSession:
      "Regroupe une même visite, afin que des pages consultées l'une après l'autre comptent pour une seule.",
    clarityVisitor:
      "Reconnaît votre navigateur d'une visite à l'autre, pour voir où les visiteurs bloquent.",
    claritySession: 'Marque la visite en cours.',
    clarityId: 'Déposé par clarity.ms, le service qui effectue ces mesures.',
    adsClick: "Relie une inscription à l'annonce dont elle provient.",
    metaBrowser:
      'Reconnaît votre navigateur, afin de mesurer nos annonces sur Facebook et Instagram.',
    metaClick: 'Déposé lorsque vous arrivez via une annonce sur Facebook ou Instagram.',
    expiry24m: '24 mois',
    expiry90d: '90 jours',
    expiry1d: '1 jour',
    adsFromAd:
      'Déposé lorsque vous arrivez via une annonce Google, et relie cette visite à celle-ci.',
    remarketingId:
      "Reconnaît votre navigateur sur d'autres sites, afin de montrer nos annonces aux personnes qui nous ont visités.",
    remarketingProbe: 'Vérifie si votre navigateur accepte les cookies publicitaires.',
    expiry13m: '13 mois',
    expiry15min: '15 minutes',
    attributionCampaign:
      'La campagne qui vous a amené ici, pour que votre inscription puisse y être liée.',
    attributionClick:
      "L'annonce sur laquelle vous avez cliqué pour arriver ici, pour que votre inscription puisse y être liée.",
    eventMarkers:
      "Retient que votre inscription a déjà été comptée, pour qu'elle ne le soit pas deux fois.",
    expiryStorage90d: 'Stockage local, 90 jours maximum',
  },

  de: {
    title: 'Wir verwenden Cookies',
    description:
      'Einige Cookies sind für den Betrieb der Website erforderlich. Mit Ihrer Erlaubnis würden wir sie auch verwenden, um zu verstehen, wie die Website genutzt wird, und um unser Marketing zu messen.',
    acceptAll: 'Alle akzeptieren',
    rejectAll: 'Nicht notwendige ablehnen',
    manage: 'Einstellungen verwalten',
    prefTitle: 'Cookie-Einstellungen',
    save: 'Einstellungen speichern',
    close: 'Schließen',
    introTitle: 'Wie wir Cookies verwenden',
    introBody:
      'Notwendige Cookies sind immer aktiv, weil die Website ohne sie nicht funktioniert. Alles andere bleibt aus, bis Sie es einschalten, und Sie können Ihre Wahl jederzeit ändern.',
    necessaryTitle: 'Unbedingt erforderlich',
    necessaryBody:
      'Merken sich die gewählte Sprache, bewahren die laufende Registrierung und speichern Ihre Cookie-Entscheidung. Sie können nicht deaktiviert werden.',
    analyticsTitle: 'Statistik',
    analyticsBody:
      'Helfen uns zu sehen, welche Seiten genutzt werden und wo Besucher hängen bleiben, damit wir sie verbessern können.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Dienen dazu, unsere Werbung zu messen und Inhalte anzuzeigen, die für Sie relevant sind.',
    preferencesTitle: 'Präferenzen',
    preferencesBody:
      'Merken sich optionale Einstellungen, damit die Website so bleibt, wie Sie sie verlassen haben. Sie bewahren auch Ihre Support-Chat-Unterhaltung auf, damit Sie dort weitermachen können, wo Sie aufgehört haben.',
    colName: 'Name',
    colPurpose: 'Zweck',
    colExpiry: 'Ablauf',
    localeCookie: 'Die von Ihnen gewählte Sprache der Website.',
    consentCookie: 'Ihre Cookie-Entscheidungen.',
    expiry12m: '12 Monate',
    expiry6m: '6 Monate',
    signupState: 'Die Registrierung, die Sie gerade abschließen.',
    supportChat:
      'Ihre Support-Chat-Unterhaltung, damit sie dort fortgesetzt wird, wo Sie aufgehört haben.',
    expiryStorage: 'Lokaler Speicher, bis Sie ihn löschen',
    gaVisitor:
      'Ob Sie schon einmal hier waren, damit ein wiederkehrender Besucher nicht als neu gezählt wird.',
    gaSession:
      'Fasst einen Besuch zusammen, damit nacheinander gelesene Seiten als ein Besuch zählen.',
    clarityVisitor:
      'Erkennt Ihren Browser zwischen Besuchen, damit wir sehen, wo Besucher hängen bleiben.',
    claritySession: 'Kennzeichnet den aktuellen Besuch.',
    clarityId: 'Von clarity.ms gesetzt, dem Dienst, der diese Messungen durchführt.',
    adsClick: 'Verknüpft eine Registrierung mit der Anzeige, aus der sie stammt.',
    metaBrowser:
      'Erkennt Ihren Browser, damit wir unsere Werbung auf Facebook und Instagram messen können.',
    metaClick: 'Wird gesetzt, wenn Sie über eine Anzeige auf Facebook oder Instagram kommen.',
    expiry24m: '24 Monate',
    expiry90d: '90 Tage',
    expiry1d: '1 Tag',
    adsFromAd:
      'Wird gesetzt, wenn Sie über eine Google-Anzeige kommen, und verknüpft diesen Besuch damit.',
    remarketingId:
      'Erkennt Ihren Browser auf anderen Websites, damit wir unsere Anzeigen Besuchern erneut zeigen können.',
    remarketingProbe: 'Prüft, ob Ihr Browser Werbe-Cookies überhaupt akzeptiert.',
    expiry13m: '13 Monate',
    expiry15min: '15 Minuten',
    attributionCampaign:
      'Die Kampagne, über die Sie hierher gekommen sind, damit Ihre Registrierung ihr zugeordnet werden kann.',
    attributionClick:
      'Die Anzeige, auf die Sie geklickt haben, um hierher zu kommen, damit Ihre Registrierung ihr zugeordnet werden kann.',
    eventMarkers:
      'Merkt sich, dass Ihre Registrierung bereits gezählt wurde, damit sie nicht doppelt zählt.',
    expiryStorage90d: 'Lokaler Speicher, höchstens 90 Tage',
  },

  es: {
    title: 'Usamos cookies',
    description:
      'Algunas cookies son necesarias para que el sitio funcione. Con su permiso, también las usaríamos para entender cómo se usa el sitio y para medir nuestro marketing.',
    acceptAll: 'Aceptar todo',
    rejectAll: 'Rechazar las no esenciales',
    manage: 'Gestionar preferencias',
    prefTitle: 'Preferencias de cookies',
    save: 'Guardar preferencias',
    close: 'Cerrar',
    introTitle: 'Cómo usamos las cookies',
    introBody:
      'Las cookies necesarias están siempre activas, porque el sitio no puede funcionar sin ellas. Todo lo demás permanece desactivado hasta que usted lo active, y puede cambiar su elección en cualquier momento.',
    necessaryTitle: 'Estrictamente necesarias',
    necessaryBody:
      'Recuerdan el idioma elegido, conservan el registro que está completando y guardan su elección de cookies. No se pueden desactivar.',
    analyticsTitle: 'Analíticas',
    analyticsBody:
      'Nos ayudan a ver qué páginas se usan y dónde se atascan los visitantes, para poder mejorarlas.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Se usan para medir nuestra publicidad y mostrar contenido que sea relevante para usted.',
    preferencesTitle: 'Preferencias',
    preferencesBody:
      'Recuerdan las opciones que elija, para que el sitio se comporte como lo dejó. También conservan su conversación con el soporte, para retomarla donde la dejó.',
    colName: 'Nombre',
    colPurpose: 'Finalidad',
    colExpiry: 'Caducidad',
    localeCookie: 'El idioma que eligió para el sitio.',
    consentCookie: 'Sus preferencias de cookies.',
    expiry12m: '12 meses',
    expiry6m: '6 meses',
    signupState: 'El registro que está completando.',
    supportChat: 'Su conversación con soporte, para continuarla donde la dejó.',
    expiryStorage: 'Almacenamiento local, hasta que lo borre',
    gaVisitor: 'Si ya ha estado aquí, para que un visitante que vuelve no se cuente como nuevo.',
    gaSession: 'Agrupa una misma visita, para que las páginas vistas seguidas cuenten como una.',
    clarityVisitor:
      'Reconoce su navegador entre visitas, para ver dónde se atascan los visitantes.',
    claritySession: 'Marca la visita actual.',
    clarityId: 'Lo establece clarity.ms, el servicio que realiza esas mediciones.',
    adsClick: 'Vincula un registro con el anuncio del que procede.',
    metaBrowser: 'Reconoce su navegador para medir nuestra publicidad en Facebook e Instagram.',
    metaClick: 'Se establece cuando llega desde un anuncio en Facebook o Instagram.',
    expiry24m: '24 meses',
    expiry90d: '90 días',
    expiry1d: '1 día',
    adsFromAd: 'Se establece cuando llega desde un anuncio de Google y vincula esta visita con él.',
    remarketingId:
      'Reconoce su navegador en otros sitios, para mostrar nuestros anuncios a quienes nos han visitado.',
    remarketingProbe: 'Comprueba si su navegador acepta cookies publicitarias.',
    expiry13m: '13 meses',
    expiry15min: '15 minutos',
    attributionCampaign:
      'La campaña que le trajo hasta aquí, para que su registro pueda vincularse a ella.',
    attributionClick:
      'El anuncio en el que hizo clic para llegar, para que su registro pueda vincularse a él.',
    eventMarkers: 'Recuerda que su registro ya se ha contado, para que no se cuente dos veces.',
    expiryStorage90d: 'Almacenamiento local, hasta 90 días',
  },

  lt: {
    title: 'Naudojame slapukus',
    description:
      'Kai kurie slapukai būtini, kad svetainė veiktų. Gavę jūsų sutikimą, juos naudotume ir tam, kad suprastume, kaip svetainė naudojama, ir įvertintume savo rinkodarą.',
    acceptAll: 'Priimti visus',
    rejectAll: 'Atmesti nebūtinus',
    manage: 'Tvarkyti nustatymus',
    prefTitle: 'Slapukų nustatymai',
    save: 'Išsaugoti nustatymus',
    close: 'Uždaryti',
    introTitle: 'Kaip naudojame slapukus',
    introBody:
      'Būtini slapukai visada įjungti, nes be jų svetainė neveikia. Visa kita lieka išjungta, kol patys neįjungsite, o savo pasirinkimą galite pakeisti bet kada.',
    necessaryTitle: 'Būtinieji',
    necessaryBody:
      'Įsimena pasirinktą kalbą, išsaugo pradėtą registraciją ir jūsų sprendimą dėl slapukų. Jų išjungti negalima.',
    analyticsTitle: 'Analitiniai',
    analyticsBody:
      'Padeda matyti, kurie puslapiai naudojami ir kur lankytojai užstringa, kad galėtume juos patobulinti.',
    marketingTitle: 'Rinkodaros',
    marketingBody: 'Naudojami mūsų reklamai įvertinti ir jums aktualiam turiniui rodyti.',
    preferencesTitle: 'Nuostatos',
    preferencesBody:
      'Įsimena pasirenkamus nustatymus, kad svetainė veiktų taip, kaip ją palikote. Jie taip pat išsaugo jūsų pokalbį su pagalbos tarnyba, kad galėtumėte jį tęsti.',
    colName: 'Pavadinimas',
    colPurpose: 'Paskirtis',
    colExpiry: 'Galiojimas',
    localeCookie: 'Jūsų pasirinkta svetainės kalba.',
    consentCookie: 'Jūsų sprendimai dėl slapukų.',
    expiry12m: '12 mėnesių',
    expiry6m: '6 mėnesiai',
    signupState: 'Registracija, kurią šiuo metu pildote.',
    supportChat:
      'Jūsų pokalbis su pagalbos tarnyba, kad galėtumėte tęsti nuo tos vietos, kur baigėte.',
    expiryStorage: 'Vietinė saugykla, kol ištrinsite',
    gaVisitor:
      'Ar jau lankėtės čia anksčiau, kad grįžtantis lankytojas nebūtų skaičiuojamas kaip naujas.',
    gaSession:
      'Sujungia vieną apsilankymą, kad iš eilės skaitomi puslapiai būtų skaičiuojami kaip vienas.',
    clarityVisitor:
      'Atpažįsta jūsų naršyklę tarp apsilankymų, kad matytume, kur lankytojai užstringa.',
    claritySession: 'Pažymi dabartinį apsilankymą.',
    clarityId: 'Nustato clarity.ms – paslauga, atliekanti šiuos matavimus.',
    adsClick: 'Susieja registraciją su skelbimu, iš kurio ji atėjo.',
    metaBrowser:
      'Atpažįsta jūsų naršyklę, kad galėtume įvertinti savo reklamą „Facebook“ ir „Instagram“.',
    metaClick: 'Nustatomas, kai atvykstate iš „Facebook“ ar „Instagram“ skelbimo.',
    expiry24m: '24 mėnesiai',
    expiry90d: '90 dienų',
    expiry1d: '1 diena',
    adsFromAd: 'Nustatomas, kai atvykstate iš „Google“ skelbimo, ir susieja šį apsilankymą su juo.',
    remarketingId:
      'Atpažįsta jūsų naršyklę kitose svetainėse, kad galėtume rodyti reklamą mus aplankiusiems.',
    remarketingProbe: 'Patikrina, ar jūsų naršyklė apskritai priima reklamos slapukus.',
    expiry13m: '13 mėnesių',
    expiry15min: '15 minučių',
    attributionCampaign:
      'Kampanija, per kurią čia patekote, kad Jūsų registraciją būtų galima su ja susieti.',
    attributionClick:
      'Reklama, kurią paspaudę čia patekote, kad Jūsų registraciją būtų galima su ja susieti.',
    eventMarkers:
      'Įsimena, kad Jūsų registracija jau įskaičiuota, kad nebūtų skaičiuojama du kartus.',
    expiryStorage90d: 'Vietinė saugykla, iki 90 dienų',
  },

  it: {
    title: 'Usiamo i cookie',
    description:
      'Alcuni cookie sono necessari al funzionamento del sito. Con il suo consenso li useremmo anche per capire come viene usato il sito e per misurare il nostro marketing.',
    acceptAll: 'Accetta tutti',
    rejectAll: 'Rifiuta i non essenziali',
    manage: 'Gestisci preferenze',
    prefTitle: 'Preferenze sui cookie',
    save: 'Salva preferenze',
    close: 'Chiudi',
    introTitle: 'Come usiamo i cookie',
    introBody:
      'I cookie necessari sono sempre attivi, perché senza di essi il sito non funziona. Tutto il resto resta disattivato finché non lo attiva lei, e può cambiare scelta in qualsiasi momento.',
    necessaryTitle: 'Strettamente necessari',
    necessaryBody:
      'Ricordano la lingua scelta, conservano la registrazione in corso e salvano la sua scelta sui cookie. Non possono essere disattivati.',
    analyticsTitle: 'Analitici',
    analyticsBody:
      'Ci aiutano a vedere quali pagine vengono usate e dove i visitatori si bloccano, per poterle migliorare.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Servono a misurare la nostra pubblicità e a mostrare contenuti rilevanti per lei.',
    preferencesTitle: 'Preferenze',
    preferencesBody:
      'Ricordano le scelte facoltative che effettua, così il sito resta come l’ha lasciato. Conservano anche la sua conversazione con l’assistenza, così può riprenderla da dove l’ha lasciata.',
    colName: 'Nome',
    colPurpose: 'Finalità',
    colExpiry: 'Scadenza',
    localeCookie: 'La lingua che ha scelto per il sito.',
    consentCookie: 'Le sue scelte sui cookie.',
    expiry12m: '12 mesi',
    expiry6m: '6 mesi',
    signupState: 'La registrazione che sta completando.',
    supportChat: 'La sua conversazione con l’assistenza, per riprenderla da dove l’aveva lasciata.',
    expiryStorage: 'Archiviazione locale, fino alla cancellazione',
    gaVisitor:
      'Se ha già visitato il sito, così una visita di ritorno non viene contata come nuova.',
    gaSession: 'Tiene insieme una visita, così le pagine lette una dopo l’altra contano come una.',
    clarityVisitor:
      'Riconosce il suo browser tra una visita e l’altra, per vedere dove i visitatori si bloccano.',
    claritySession: 'Contrassegna la visita in corso.',
    clarityId: 'Impostato da clarity.ms, il servizio che effettua queste misurazioni.',
    adsClick: 'Collega una registrazione all’annuncio da cui proviene.',
    metaBrowser:
      'Riconosce il suo browser, per misurare la nostra pubblicità su Facebook e Instagram.',
    metaClick: 'Impostato quando arriva da un annuncio su Facebook o Instagram.',
    expiry24m: '24 mesi',
    expiry90d: '90 giorni',
    expiry1d: '1 giorno',
    adsFromAd:
      'Impostato quando arriva da un annuncio Google e collega questa visita a quell’annuncio.',
    remarketingId:
      'Riconosce il suo browser su altri siti, per mostrare i nostri annunci a chi ci ha visitato.',
    remarketingProbe: 'Verifica se il suo browser accetta i cookie pubblicitari.',
    expiry13m: '13 mesi',
    expiry15min: '15 minuti',
    attributionCampaign:
      'La campagna che l’ha portata qui, perché la sua registrazione possa esserle collegata.',
    attributionClick:
      'L’annuncio su cui ha cliccato per arrivare qui, perché la sua registrazione possa esservi collegata.',
    eventMarkers:
      'Ricorda che la sua registrazione è già stata conteggiata, perché non venga contata due volte.',
    expiryStorage90d: 'Archiviazione locale, fino a 90 giorni',
  },

  pt: {
    title: 'Utilizamos cookies',
    description:
      'Alguns cookies são necessários para o funcionamento do site. Com a sua autorização, também os usaríamos para perceber como o site é utilizado e para medir o nosso marketing.',
    acceptAll: 'Aceitar tudo',
    rejectAll: 'Recusar os não essenciais',
    manage: 'Gerir preferências',
    prefTitle: 'Preferências de cookies',
    save: 'Guardar preferências',
    close: 'Fechar',
    introTitle: 'Como utilizamos os cookies',
    introBody:
      'Os cookies necessários estão sempre ativos, porque o site não funciona sem eles. Tudo o resto fica desativado até que os ative, e pode alterar a sua escolha a qualquer momento.',
    necessaryTitle: 'Estritamente necessários',
    necessaryBody:
      'Guardam o idioma escolhido, mantêm o registo que está a concluir e guardam a sua escolha de cookies. Não podem ser desativados.',
    analyticsTitle: 'Analíticos',
    analyticsBody:
      'Ajudam-nos a ver que páginas são usadas e onde os visitantes ficam presos, para as podermos melhorar.',
    marketingTitle: 'Marketing',
    marketingBody: 'Usados para medir a nossa publicidade e mostrar conteúdos relevantes para si.',
    preferencesTitle: 'Preferências',
    preferencesBody:
      'Guardam as escolhas opcionais que faz, para que o site fique como o deixou. Também guardam a sua conversa com o suporte, para a retomar onde a deixou.',
    colName: 'Nome',
    colPurpose: 'Finalidade',
    colExpiry: 'Validade',
    localeCookie: 'O idioma que escolheu para o site.',
    consentCookie: 'As suas escolhas de cookies.',
    expiry12m: '12 meses',
    expiry6m: '6 meses',
    signupState: 'O registo que está a concluir.',
    supportChat: 'A sua conversa com o apoio, para continuar onde parou.',
    expiryStorage: 'Armazenamento local, até que o limpe',
    gaVisitor:
      'Se já esteve aqui antes, para que um visitante que regressa não seja contado como novo.',
    gaSession: 'Mantém uma visita junta, para que páginas lidas seguidas contem como uma.',
    clarityVisitor:
      'Reconhece o seu navegador entre visitas, para vermos onde os visitantes ficam presos.',
    claritySession: 'Marca a visita atual.',
    clarityId: 'Definido por clarity.ms, o serviço que faz essas medições.',
    adsClick: 'Liga um registo ao anúncio de onde veio.',
    metaBrowser:
      'Reconhece o seu navegador, para medirmos a nossa publicidade no Facebook e no Instagram.',
    metaClick: 'Definido quando chega através de um anúncio no Facebook ou no Instagram.',
    expiry24m: '24 meses',
    expiry90d: '90 dias',
    expiry1d: '1 dia',
    adsFromAd:
      'Definido quando chega através de um anúncio Google e liga esta visita a esse anúncio.',
    remarketingId:
      'Reconhece o seu navegador noutros sites, para mostrarmos os nossos anúncios a quem nos visitou.',
    remarketingProbe: 'Verifica se o seu navegador aceita cookies de publicidade.',
    expiry13m: '13 meses',
    expiry15min: '15 minutos',
    attributionCampaign:
      'A campanha que o trouxe até aqui, para que o seu registo lhe possa ser associado.',
    attributionClick:
      'O anúncio em que clicou para chegar aqui, para que o seu registo lhe possa ser associado.',
    eventMarkers: 'Lembra que o seu registo já foi contado, para que não seja contado duas vezes.',
    expiryStorage90d: 'Armazenamento local, até 90 dias',
  },

  pl: {
    title: 'Używamy plików cookie',
    description:
      'Niektóre pliki cookie są niezbędne do działania strony. Za Twoją zgodą używalibyśmy ich również, aby rozumieć, jak strona jest używana, i mierzyć nasz marketing.',
    acceptAll: 'Zaakceptuj wszystkie',
    rejectAll: 'Odrzuć zbędne',
    manage: 'Zarządzaj ustawieniami',
    prefTitle: 'Ustawienia plików cookie',
    save: 'Zapisz ustawienia',
    close: 'Zamknij',
    introTitle: 'Jak używamy plików cookie',
    introBody:
      'Niezbędne pliki cookie są zawsze włączone, ponieważ bez nich strona nie działa. Wszystko inne pozostaje wyłączone, dopóki tego nie włączysz, a swój wybór możesz zmienić w każdej chwili.',
    necessaryTitle: 'Niezbędne',
    necessaryBody:
      'Zapamiętują wybrany język, zachowują rozpoczętą rejestrację i przechowują Twój wybór dotyczący plików cookie. Nie można ich wyłączyć.',
    analyticsTitle: 'Analityczne',
    analyticsBody:
      'Pomagają nam zobaczyć, które strony są używane i gdzie odwiedzający się zatrzymują, abyśmy mogli je ulepszyć.',
    marketingTitle: 'Marketingowe',
    marketingBody: 'Służą do mierzenia naszych reklam i pokazywania treści istotnych dla Ciebie.',
    preferencesTitle: 'Preferencje',
    preferencesBody:
      'Zapamiętują opcjonalne wybory, aby strona działała tak, jak ją zostawiłeś. Zachowują też rozmowę z pomocą techniczną, aby można było ją kontynuować.',
    colName: 'Nazwa',
    colPurpose: 'Cel',
    colExpiry: 'Wygasa',
    localeCookie: 'Wybrany przez Ciebie język strony.',
    consentCookie: 'Twoje wybory dotyczące plików cookie.',
    expiry12m: '12 miesięcy',
    expiry6m: '6 miesięcy',
    signupState: 'Rejestracja, którą właśnie wypełniasz.',
    supportChat:
      'Twoja rozmowa ze wsparciem, aby kontynuować ją w miejscu, w którym została przerwana.',
    expiryStorage: 'Pamięć lokalna, do wyczyszczenia',
    gaVisitor:
      'Czy ta przeglądarka była tu wcześniej, aby powracający odwiedzający nie był liczony jako nowy.',
    gaSession: 'Łączy jedną wizytę, aby strony czytane po kolei liczyły się jako jedna.',
    clarityVisitor:
      'Rozpoznaje przeglądarkę między wizytami, abyśmy widzieli, gdzie odwiedzający się zatrzymują.',
    claritySession: 'Oznacza bieżącą wizytę.',
    clarityId: 'Ustawiany przez clarity.ms, usługę wykonującą te pomiary.',
    adsClick: 'Łączy rejestrację z reklamą, z której pochodzi.',
    metaBrowser:
      'Rozpoznaje przeglądarkę, abyśmy mogli mierzyć naszą reklamę na Facebooku i Instagramie.',
    metaClick: 'Ustawiany, gdy trafiasz tu z reklamy na Facebooku lub Instagramie.',
    expiry24m: '24 miesiące',
    expiry90d: '90 dni',
    expiry1d: '1 dzień',
    adsFromAd: 'Ustawiany, gdy trafiasz tu z reklamy Google, i łączy tę wizytę z tą reklamą.',
    remarketingId:
      'Rozpoznaje przeglądarkę na innych stronach, abyśmy mogli pokazywać reklamy osobom, które nas odwiedziły.',
    remarketingProbe: 'Sprawdza, czy przeglądarka w ogóle akceptuje reklamowe pliki cookie.',
    expiry13m: '13 miesięcy',
    expiry15min: '15 minut',
    attributionCampaign:
      'Kampania, z której pochodzi Twoja wizyta, aby można było powiązać z nią Twoją rejestrację.',
    attributionClick:
      'Reklama, z której przyszła Twoja wizyta, aby można było powiązać z nią Twoją rejestrację.',
    eventMarkers:
      'Zapamiętuje, że Twoja rejestracja została już policzona, aby nie liczyć jej dwa razy.',
    expiryStorage90d: 'Pamięć lokalna, do 90 dni',
  },

  ro: {
    title: 'Folosim module cookie',
    description:
      'Unele module cookie sunt necesare pentru funcționarea site-ului. Cu permisiunea dumneavoastră le-am folosi și pentru a înțelege cum este utilizat site-ul și pentru a măsura marketingul nostru.',
    acceptAll: 'Acceptă toate',
    rejectAll: 'Respinge cele neesențiale',
    manage: 'Gestionează preferințele',
    prefTitle: 'Preferințe pentru cookie-uri',
    save: 'Salvează preferințele',
    close: 'Închide',
    introTitle: 'Cum folosim modulele cookie',
    introBody:
      'Modulele cookie necesare sunt mereu active, deoarece site-ul nu poate funcționa fără ele. Restul rămâne dezactivat până când le activați, iar alegerea poate fi schimbată oricând.',
    necessaryTitle: 'Strict necesare',
    necessaryBody:
      'Rețin limba aleasă, păstrează înregistrarea în curs și salvează alegerea privind cookie-urile. Acestea nu pot fi dezactivate.',
    analyticsTitle: 'Analitice',
    analyticsBody:
      'Ne ajută să vedem ce pagini sunt folosite și unde se blochează vizitatorii, ca să le putem îmbunătăți.',
    marketingTitle: 'Marketing',
    marketingBody:
      'Folosite pentru a măsura publicitatea noastră și a afișa conținut relevant pentru dumneavoastră.',
    preferencesTitle: 'Preferințe',
    preferencesBody:
      'Rețin opțiunile facultative pe care le alegeți, astfel încât site-ul să rămână așa cum l-ați lăsat. Păstrează și conversația dvs. cu echipa de asistență, ca să o puteți relua de unde ați rămas.',
    colName: 'Nume',
    colPurpose: 'Scop',
    colExpiry: 'Expiră',
    localeCookie: 'Limba pe care ați ales-o pentru site.',
    consentCookie: 'Alegerile dumneavoastră privind cookie-urile.',
    expiry12m: '12 luni',
    expiry6m: '6 luni',
    signupState: 'Înregistrarea pe care o completați.',
    supportChat: 'Conversația dvs. cu asistența, pentru a continua de unde ați rămas.',
    expiryStorage: 'Stocare locală, până la ștergere',
    gaVisitor:
      'Dacă ați mai fost aici, astfel încât un vizitator care revine să nu fie numărat ca nou.',
    gaSession: 'Grupează o vizită, astfel încât paginile citite una după alta să conteze ca una.',
    clarityVisitor:
      'Vă recunoaște browserul între vizite, ca să vedem unde se blochează vizitatorii.',
    claritySession: 'Marchează vizita curentă.',
    clarityId: 'Setat de clarity.ms, serviciul care face aceste măsurători.',
    adsClick: 'Leagă o înregistrare de anunțul din care a venit.',
    metaBrowser:
      'Vă recunoaște browserul, ca să putem măsura reclamele noastre pe Facebook și Instagram.',
    metaClick: 'Setat când ajungeți dintr-un anunț de pe Facebook sau Instagram.',
    expiry24m: '24 de luni',
    expiry90d: '90 de zile',
    expiry1d: '1 zi',
    adsFromAd: 'Setat când ajungeți dintr-un anunț Google și leagă această vizită de el.',
    remarketingId:
      'Vă recunoaște browserul pe alte site-uri, ca să arătăm reclamele noastre celor care ne-au vizitat.',
    remarketingProbe: 'Verifică dacă browserul acceptă cookie-uri publicitare.',
    expiry13m: '13 luni',
    expiry15min: '15 minute',
    attributionCampaign:
      'Campania prin care ați ajuns aici, pentru ca înregistrarea dvs. să poată fi asociată cu ea.',
    attributionClick:
      'Anunțul pe care ați dat clic pentru a ajunge aici, pentru ca înregistrarea dvs. să poată fi asociată cu el.',
    eventMarkers:
      'Reține că înregistrarea dvs. a fost deja numărată, pentru a nu fi numărată de două ori.',
    expiryStorage90d: 'Stocare locală, cel mult 90 de zile',
  },

  bg: {
    title: 'Използваме бисквитки',
    description:
      'Някои бисквитки са необходими, за да работи сайтът. С вашето съгласие бихме ги използвали и за да разберем как се използва сайтът и да измерим маркетинга си.',
    acceptAll: 'Приемам всички',
    rejectAll: 'Отхвърляне на несъществените',
    manage: 'Управление на настройките',
    prefTitle: 'Настройки за бисквитки',
    save: 'Запазване на настройките',
    close: 'Затваряне',
    introTitle: 'Как използваме бисквитките',
    introBody:
      'Необходимите бисквитки са винаги включени, защото без тях сайтът не работи. Всичко останало остава изключено, докато вие не го включите, и можете да промените избора си по всяко време.',
    necessaryTitle: 'Строго необходими',
    necessaryBody:
      'Запомнят избрания език, пазят започнатата регистрация и съхраняват избора ви за бисквитки. Не могат да бъдат изключени.',
    analyticsTitle: 'Аналитични',
    analyticsBody:
      'Помагат ни да видим кои страници се използват и къде посетителите се затрудняват, за да ги подобрим.',
    marketingTitle: 'Маркетингови',
    marketingBody:
      'Използват се, за да измерваме рекламата си и да показваме съдържание, което е подходящо за вас.',
    preferencesTitle: 'Предпочитания',
    preferencesBody:
      'Запомнят незадължителните ви избори, за да работи сайтът така, както сте го оставили. Запазват и разговора ви с поддръжката, за да го продължите оттам, докъдето сте стигнали.',
    colName: 'Име',
    colPurpose: 'Цел',
    colExpiry: 'Срок',
    localeCookie: 'Езикът, който сте избрали за сайта.',
    consentCookie: 'Вашият избор за бисквитките.',
    expiry12m: '12 месеца',
    expiry6m: '6 месеца',
    signupState: 'Регистрацията, която попълвате.',
    supportChat: 'Вашият разговор с поддръжката, за да продължи оттам, докъдето сте стигнали.',
    expiryStorage: 'Локално хранилище, докато не го изчистите',
    gaVisitor: 'Дали вече сте били тук, за да не се брои завърналият се посетител като нов.',
    gaSession: 'Свързва едно посещение, за да се броят последователно четените страници като едно.',
    clarityVisitor:
      'Разпознава браузъра ви между посещенията, за да видим къде посетителите се затрудняват.',
    claritySession: 'Отбелязва текущото посещение.',
    clarityId: 'Задава се от clarity.ms — услугата, която прави тези измервания.',
    adsClick: 'Свързва регистрацията с рекламата, от която идва.',
    metaBrowser: 'Разпознава браузъра ви, за да измерваме рекламата си във Facebook и Instagram.',
    metaClick: 'Задава се, когато идвате от реклама във Facebook или Instagram.',
    expiry24m: '24 месеца',
    expiry90d: '90 дни',
    expiry1d: '1 ден',
    adsFromAd: 'Задава се, когато идвате от реклама в Google, и свързва това посещение с нея.',
    remarketingId:
      'Разпознава браузъра ви в други сайтове, за да показваме рекламите си на посещавалите ни.',
    remarketingProbe: 'Проверява дали браузърът ви изобщо приема рекламни бисквитки.',
    expiry13m: '13 месеца',
    expiry15min: '15 минути',
    attributionCampaign:
      'Кампанията, от която сте дошли, за да може регистрацията ви да бъде свързана с нея.',
    attributionClick:
      'Рекламата, върху която сте кликнали, за да може регистрацията ви да бъде свързана с нея.',
    eventMarkers: 'Запомня, че регистрацията ви вече е отчетена, за да не се отчете два пъти.',
    expiryStorage90d: 'Локално хранилище, до 90 дни',
  },

  tr: {
    title: 'Çerez kullanıyoruz',
    description:
      'Bazı çerezler sitenin çalışması için gereklidir. İzniniz olursa bunları sitenin nasıl kullanıldığını anlamak ve pazarlamamızı ölçmek için de kullanırdık.',
    acceptAll: 'Tümünü kabul et',
    rejectAll: 'Zorunlu olmayanları reddet',
    manage: 'Tercihleri yönet',
    prefTitle: 'Çerez tercihleri',
    save: 'Tercihleri kaydet',
    close: 'Kapat',
    introTitle: 'Çerezleri nasıl kullanıyoruz',
    introBody:
      'Gerekli çerezler her zaman açıktır, çünkü site onlarsız çalışmaz. Diğer her şey siz açana kadar kapalı kalır ve seçiminizi istediğiniz zaman değiştirebilirsiniz.',
    necessaryTitle: 'Kesinlikle gerekli',
    necessaryBody:
      'Seçtiğiniz dili hatırlar, sürmekte olan kaydınızı saklar ve çerez tercihinizi tutar. Bunlar kapatılamaz.',
    analyticsTitle: 'Analitik',
    analyticsBody:
      'Hangi sayfaların kullanıldığını ve ziyaretçilerin nerede takıldığını görmemize yardımcı olur, böylece onları iyileştirebiliriz.',
    marketingTitle: 'Pazarlama',
    marketingBody: 'Reklamlarımızı ölçmek ve sizinle ilgili içerik göstermek için kullanılır.',
    preferencesTitle: 'Tercihler',
    preferencesBody:
      'Yaptığınız isteğe bağlı seçimleri hatırlar, böylece site bıraktığınız gibi davranır. Ayrıca destek sohbetinizi saklar, böylece kaldığınız yerden devam edebilirsiniz.',
    colName: 'Ad',
    colPurpose: 'Amaç',
    colExpiry: 'Süre',
    localeCookie: 'Site için seçtiğiniz dil.',
    consentCookie: 'Çerez tercihleriniz.',
    expiry12m: '12 ay',
    expiry6m: '6 ay',
    signupState: 'Tamamlamakta olduğunuz kayıt.',
    supportChat: 'Destek sohbetiniz, kaldığınız yerden devam etmesi için.',
    expiryStorage: 'Yerel depolama, siz silene kadar',
    gaVisitor:
      'Daha önce burada bulunup bulunmadığınız; böylece geri dönen ziyaretçi yeni sayılmaz.',
    gaSession: 'Tek bir ziyareti bir arada tutar; art arda okunan sayfalar tek ziyaret sayılır.',
    clarityVisitor:
      'Ziyaretler arasında tarayıcınızı tanır; böylece ziyaretçilerin nerede takıldığını görürüz.',
    claritySession: 'Şu anki ziyareti işaretler.',
    clarityId: 'Bu ölçümleri yapan clarity.ms hizmeti tarafından ayarlanır.',
    adsClick: 'Bir kaydı, geldiği reklamla ilişkilendirir.',
    metaBrowser: 'Facebook ve Instagram reklamlarımızı ölçebilmemiz için tarayıcınızı tanır.',
    metaClick: "Facebook veya Instagram'daki bir reklamdan geldiğinizde ayarlanır.",
    expiry24m: '24 ay',
    expiry90d: '90 gün',
    expiry1d: '1 gün',
    adsFromAd:
      'Bir Google reklamından geldiğinizde ayarlanır ve bu ziyareti o reklamla ilişkilendirir.',
    remarketingId:
      'Tarayıcınızı başka sitelerde tanır; böylece bizi ziyaret edenlere reklamlarımızı gösterebiliriz.',
    remarketingProbe: 'Tarayıcınızın reklam çerezlerini kabul edip etmediğini kontrol eder.',
    expiry13m: '13 ay',
    expiry15min: '15 dakika',
    attributionCampaign:
      'Sizi buraya getiren kampanya; kaydınızın onunla ilişkilendirilebilmesi için.',
    attributionClick:
      'Buraya gelmek için tıkladığınız reklam; kaydınızın onunla ilişkilendirilebilmesi için.',
    eventMarkers: 'Kaydınızın zaten sayıldığını hatırlar; iki kez sayılmaması için.',
    expiryStorage90d: 'Yerel depolama, en fazla 90 gün',
  },

  no: {
    title: 'Vi bruker informasjonskapsler',
    description:
      'Noen informasjonskapsler er nødvendige for at nettstedet skal fungere. Med ditt samtykke ville vi også brukt dem til å forstå hvordan nettstedet brukes og til å måle markedsføringen vår.',
    acceptAll: 'Godta alle',
    rejectAll: 'Avvis ikke-nødvendige',
    manage: 'Administrer innstillinger',
    prefTitle: 'Innstillinger for informasjonskapsler',
    save: 'Lagre innstillinger',
    close: 'Lukk',
    introTitle: 'Slik bruker vi informasjonskapsler',
    introBody:
      'Nødvendige informasjonskapsler er alltid på, fordi nettstedet ikke fungerer uten dem. Alt annet er av til du slår det på, og du kan endre valget ditt når som helst.',
    necessaryTitle: 'Strengt nødvendige',
    necessaryBody:
      'Husker språket du valgte, tar vare på registreringen du holder på med, og lagrer valget ditt om informasjonskapsler. Disse kan ikke slås av.',
    analyticsTitle: 'Analyse',
    analyticsBody:
      'Hjelper oss å se hvilke sider som brukes og hvor besøkende stopper opp, slik at vi kan forbedre dem.',
    marketingTitle: 'Markedsføring',
    marketingBody: 'Brukes til å måle annonseringen vår og vise innhold som er relevant for deg.',
    preferencesTitle: 'Preferanser',
    preferencesBody:
      'Husker valgfrie innstillinger, slik at nettstedet oppfører seg slik du forlot det. De tar også vare på supportsamtalen din, slik at du kan fortsette der du slapp.',
    colName: 'Navn',
    colPurpose: 'Formål',
    colExpiry: 'Utløper',
    localeCookie: 'Språket du valgte for nettstedet.',
    consentCookie: 'Valgene dine om informasjonskapsler.',
    expiry12m: '12 måneder',
    expiry6m: '6 måneder',
    signupState: 'Registreringen du holder på med.',
    supportChat: 'Samtalen din med kundestøtte, slik at den fortsetter der du slapp.',
    expiryStorage: 'Lokal lagring, til du sletter den',
    gaVisitor: 'Om du har vært her før, slik at en tilbakevendende besøkende ikke telles som ny.',
    gaSession: 'Holder ett besøk samlet, slik at sider lest etter hverandre teller som ett.',
    clarityVisitor: 'Gjenkjenner nettleseren din mellom besøk, så vi ser hvor besøkende står fast.',
    claritySession: 'Merker besøket du er inne i nå.',
    clarityId: 'Settes av clarity.ms, tjenesten som utfører disse målingene.',
    adsClick: 'Knytter en registrering til annonsen den kom fra.',
    metaBrowser:
      'Gjenkjenner nettleseren din, slik at vi kan måle annonseringen vår på Facebook og Instagram.',
    metaClick: 'Settes når du kommer fra en annonse på Facebook eller Instagram.',
    expiry24m: '24 måneder',
    expiry90d: '90 dager',
    expiry1d: '1 dag',
    adsFromAd: 'Settes når du kommer fra en Google-annonse, og knytter dette besøket til den.',
    remarketingId:
      'Gjenkjenner nettleseren din på andre nettsteder, så vi kan vise annonsene våre til dem som har besøkt oss.',
    remarketingProbe:
      'Sjekker om nettleseren din i det hele tatt godtar annonseinformasjonskapsler.',
    expiry13m: '13 måneder',
    expiry15min: '15 minutter',
    attributionCampaign:
      'Kampanjen som førte deg hit, slik at registreringen din kan knyttes til den.',
    attributionClick:
      'Annonsen du klikket på for å komme hit, slik at registreringen din kan knyttes til den.',
    eventMarkers:
      'Husker at registreringen din allerede er talt, slik at den ikke telles to ganger.',
    expiryStorage90d: 'Lokal lagring, opptil 90 dager',
  },
};

/** The locale the banner falls back to when a path prefix has no copy above. */
export const CONSENT_FALLBACK_LANG = 'en';

/**
 * DIT-517 — the English every other locale was translated from.
 *
 * A stale translation is neither empty nor a copy of the English, so no check on the
 * locales themselves can see one. What can be seen is the English moving: this is
 * the English as it stood when the locales were last translated, and any key whose
 * English differs from it has locales describing something else.
 *
 * The translation pass after the legal review refreshes this copy to the English it
 * translated from and empties `ENGLISH_CHANGED_SINCE_TRANSLATION`, in one change.
 *
 * An empty string is a key added since the last pass, whose locales were machine-
 * translated in the meantime: it differs from any English, so it stays on the list
 * until the pass gives it the English it was translated from.
 */
export const ENGLISH_AT_LAST_TRANSLATION: ConsentCopy = {
  title: 'We use cookies',
  description:
    'Some cookies are needed for the site to work. With your permission we would also use them to understand how the site is used and to measure our marketing.',
  acceptAll: 'Accept all',
  rejectAll: 'Reject non-essential',
  manage: 'Manage preferences',
  prefTitle: 'Cookie preferences',
  save: 'Save preferences',
  close: 'Close',
  introTitle: 'How we use cookies',
  introBody:
    'Necessary cookies are always on, because the site cannot work without them. Everything else stays off until you turn it on, and you can change your choice at any time.',
  necessaryTitle: 'Strictly necessary',
  necessaryBody:
    'Remember the language you chose, keep the signup you are completing, and store your cookie choice. These cannot be switched off.',
  analyticsTitle: 'Analytics',
  analyticsBody:
    'Help us see which pages are used and where visitors get stuck, so we can improve them.',
  marketingTitle: 'Marketing',
  marketingBody: 'Used to measure our advertising and to show content that is relevant to you.',
  preferencesTitle: 'Preferences',
  preferencesBody:
    'Remember optional choices you make, so the site behaves the way you left it. They also keep your support-chat conversation, so you can pick it up where you left off.',
  colName: 'Name',
  colPurpose: 'Purpose',
  colExpiry: 'Expiry',
  localeCookie: 'The language you chose for the site.',
  consentCookie: 'Your cookie choices.',
  expiry12m: '12 months',
  expiry6m: '6 months',
  signupState: 'The signup you are part-way through.',
  supportChat: 'Your support-chat conversation, so it continues where you left off.',
  expiryStorage: 'Local storage, until cleared',
  gaVisitor:
    'Whether you have been here before, so a returning visitor is not counted as a new one.',
  gaSession: 'Keeps one visit together, so pages read one after another count once.',
  clarityVisitor: 'Recognises your browser between visits, so we can see where people get stuck.',
  claritySession: 'Marks the visit you are in now.',
  clarityId: 'Set by clarity.ms, the service that makes those measurements.',
  adsClick: 'Links a signup back to the advertisement it came from.',
  metaBrowser:
    'Recognises your browser, so we can measure our advertising on Facebook and Instagram.',
  metaClick: 'Set when you arrive from an advertisement on Facebook or Instagram.',
  expiry24m: '24 months',
  expiry90d: '90 days',
  expiry1d: '1 day',
  adsFromAd: 'Set when you arrive from a Google advertisement, and ties this visit to it.',
  remarketingId:
    'Recognises your browser on other sites, so we can show our advertisements to people who have visited us.',
  remarketingProbe: 'Checks whether your browser accepts advertising cookies at all.',
  expiry13m: '13 months',
  expiry15min: '15 minutes',
  attributionCampaign: '',
  attributionClick: '',
  eventMarkers: '',
  expiryStorage90d: '',
};

/**
 * The four English strings the banner showed before DIT-517, while the privacy policy
 * that describes the new banner is not published (DIT-517, the draft in the CMS).
 *
 * The website shows the banner the *published* policy goes with: revision 1 until the
 * reviewed policy is published, revision 2 from that day. Revision 1 is the banner that
 * was live — these four English strings as they were, every other locale as it is,
 * because only the English was reworded — so nothing a visitor reads changes before
 * the policy does. Delete this, and revision 1 with it, once the policy is published.
 */
export const COPY_BEFORE_POLICY: Record<string, Partial<ConsentCopy>> = {
  en: {
    description:
      'Some cookies are needed for the site to work. With your permission we would also use them to understand how the site is used and to measure our marketing.',
    analyticsBody:
      'Help us see which pages are used and where visitors get stuck, so we can improve them.',
    marketingTitle: 'Marketing',
    marketingBody: 'Used to measure our advertising and to show content that is relevant to you.',
  },
};
